import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ROUTES, withPeek } from "@/app/routes";
import { yupResolver } from "@hookform/resolvers/yup";
import { CreateInvestmentDialog } from "@/components/shared/overlays/CreateInvestmentDialog";
import {
  searchInvestmentBeneficiaries,
  searchInvestmentInvestors,
} from "@/application/queries/investments/partySearch";
import { investmentFormDefaults } from "@/application/workflows/investments/investmentFormDefaults";
import type { BeneficiaryDetail } from "@/domain/beneficiary";

export interface InvestmentBeneficiaryScope {
  uuid: string;
  name: string;
}
import { InvestmentState } from "@/domain/investment/types";
import type { InvestorSummary } from "@/domain/investor";
import {
  createInvestmentFormSchema,
  createInvestmentValidationMessages,
  type InvestmentFormContext,
  type InvestmentFormData,
} from "@/domain/validation/investmentSchemas";
import { useInvestmentCreateLabels } from "@/presentation/hooks/useInvestmentDialogLabels";
import { INVESTMENT_FORM_STATES } from "@/domain/investment/stageRules";
import { useInvestmentStateLabels } from "@/presentation/hooks/useInvestmentStateLabels";
import { useInvestmentForm } from "./useInvestmentForm";
import { useInvestmentFormValues } from "./useInvestmentFormValues";
import { useRecordFormat } from "@/presentation/hooks/useRecordFormat";
import { useToastStore } from "@/stores/useToastStore";

export function useCreateInvestmentOverlay({
  onCreated,
  onView,
}: {
  onCreated?: (uuid: string) => void;
  onView?: (uuid: string) => void;
} = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const showToast = useToastStore((state) => state.showToast);
  const labels = useInvestmentCreateLabels();
  const stateLabels = useInvestmentStateLabels();
  const format = useRecordFormat();
  const [isOpen, setIsOpen] = useState(false);
  const [initialState, setInitialState] = useState<InvestmentState>(InvestmentState.PROSPECT);
  const [investor, setInvestor] = useState<InvestorSummary | null>(null);
  const [beneficiary, setBeneficiary] = useState<InvestmentBeneficiaryScope | null>(null);
  const [targetQuota, setTargetQuota] = useState<string | null>(null);
  const [proposalUuid, setProposalUuid] = useState<string | null>(null);
  const appliedQuota = useRef<string | null>(null);
  const validationContext = useRef<InvestmentFormContext>({
    hasSelectedQuota: false,
    deductionAvailable: null,
    ownDeduction: "0",
  });
  const messages = useMemo(() => createInvestmentValidationMessages(t), [t]);
  const form = useForm<InvestmentFormData>({
    mode: "onChange",
    resolver: yupResolver(createInvestmentFormSchema(messages)),
    context: validationContext.current,
    defaultValues: investmentFormDefaults(initialState),
  });
  const investmentForm = useInvestmentForm({
    form,
    validationContext,
    isOpen,
    preselectedInvestor: investor,
    preselectedProject: beneficiary as BeneficiaryDetail | null,
    preselectedState: initialState,
    proposalUuid,
    onInvestmentAdded: (uuid: string) => {
      setIsOpen(false);
      showToast({
        type: "success",
        message: t("investments.create.created"),
        action: {
          label: t("investments.create.view"),
          onClick: () =>
            onView ? onView(uuid) : navigate(withPeek(ROUTES.INVESTMENTS.MAIN, uuid)),
        },
      });
      onCreated?.(uuid);
    },
    onClose: () => setIsOpen(false),
  });
  const { quotaOptions } = investmentForm;
  useEffect(() => {
    if (!isOpen || !targetQuota || appliedQuota.current === targetQuota) return;
    if (!quotaOptions.some((option) => option.value === targetQuota)) return;
    appliedQuota.current = targetQuota;
    form.setValue("quotaId", targetQuota, { shouldValidate: true, shouldDirty: true });
  }, [isOpen, targetQuota, quotaOptions, form]);

  const quotaName = investmentForm.selectedQuota
    ? String(investmentForm.selectedQuota.fiscal_year)
    : "";
  const adapter = useInvestmentFormValues(investmentForm, quotaName);
  const errors = form.formState.errors;

  const dialog = (
    <CreateInvestmentDialog
      open={isOpen}
      onOpenChange={setIsOpen}
      labels={labels}
      isReady={form.formState.isValid}
      isCreating={investmentForm.isSubmitting}
      onCreate={() => void investmentForm.onSubmit()}
      values={adapter.values}
      locked={{ investor: investor?.display_name, beneficiary: beneficiary?.name }}
      quotas={investmentForm.quotaOptions}
      states={INVESTMENT_FORM_STATES.map((value) => ({ value, label: stateLabels[value] }))}
      quota={adapter.quota}
      transferredDeduction={adapter.transferredDeduction}
      amountError={errors.amount?.message}
      returnError={errors.returnPercentage?.message}
      format={format.amount}
      searchBeneficiaries={async (query) => (await searchInvestmentBeneficiaries(query)).options}
      searchInvestors={async (query) => (await searchInvestmentInvestors(query)).options}
      onChange={adapter.onChange}
    />
  );

  return {
    dialog,
    open: (
      state: InvestmentState | null,
      preselected: InvestorSummary | null = null,
      scope: {
        beneficiary?: InvestmentBeneficiaryScope;
        quotaUuid?: string;
        amount?: string;
        proposalUuid?: string;
      } = {},
    ) => {
      const next = state ?? InvestmentState.PROSPECT;
      setInitialState(next);
      setInvestor(preselected);
      setBeneficiary(scope.beneficiary ?? null);
      setTargetQuota(scope.quotaUuid ?? null);
      setProposalUuid(scope.proposalUuid ?? null);
      appliedQuota.current = null;
      form.reset({ ...investmentFormDefaults(next), amount: scope.amount ?? "" });
      setIsOpen(true);
    },
  };
}
